/* global Office, OfficeRuntime */

import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';

/**
 * OfficeAuth - Authentication for the PowerPoint add-in.
 *
 * Two paths, tried in order on plugin boot:
 *
 *   1. Silent SSO via OfficeRuntime.auth.getAccessToken() — when the user
 *      has signed into PowerPoint with a Microsoft 365 account, Azure AD
 *      issues a JWT silently. Backend exchanges it for a Firebase custom
 *      token. No popup, no extra password prompt.
 *
 *   2. Office Dialog API — full browser popup for Google sign-in or any
 *      case where SSO is unavailable (older Office, non-Microsoft account,
 *      tenant blocks the API, etc.). The dialog signs in with Firebase
 *      directly and posts the resulting custom token back.
 *
 * Office add-ins run in a sandboxed webview that blocks third-party
 * cookies, which is why we can't just use signInWithPopup in the taskpane
 * itself — that's what makes both paths above necessary.
 */

/**
 * Open the Office Dialog API auth window.
 *
 * @param {Object} options
 * @param {string} options.authUrl - Full URL to the auth dialog page (e.g. https://app.restack.cloud/taskpane/auth)
 * @param {number} [options.height=60] - Dialog height as % of screen
 * @param {number} [options.width=30] - Dialog width as % of screen
 * @returns {Promise<{ token: string, user: Object }>}
 */
export function openAuthDialog({ authUrl, height = 60, width = 30 }) {
  return new Promise((resolve, reject) => {
    if (typeof Office === 'undefined' || !Office.context?.ui) {
      reject(new Error('Office Dialog API not available'));
      return;
    }

    // Pass the host platform to the dialog via the URL. Inside an Office dialog
    // window, Office.context.platform is NOT reliably populated, so the dialog
    // cannot tell on its own that it is running under Office on the web (where it
    // must use signInWithPopup, not a cross-origin redirect). The TASK PANE here
    // does have a reliable platform value, so we forward it. The dialog reads
    // `hostPlatform` from its query string to choose popup vs. redirect.
    let dialogUrl = authUrl;
    try {
      const platform = (typeof Office !== 'undefined' && Office.context && Office.context.platform) || '';
      if (platform) {
        dialogUrl += (authUrl.indexOf('?') === -1 ? '?' : '&') + 'hostPlatform=' + encodeURIComponent(platform);
      }
    } catch (e) { /* fall back to the bare authUrl */ }

    Office.context.ui.displayDialogAsync(
      dialogUrl,
      { height, width, promptBeforeOpen: false },
      (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          reject(new Error(`Dialog open failed: ${asyncResult.error.message}`));
          return;
        }

        const dialog = asyncResult.value;
        let settled = false;
        let closed = false;
        let unsubAuth = () => {};
        let closeGraceTimer = null;

        // Closing the dialog is intentionally decoupled from settling the
        // promise: the Microsoft signInWithRedirect flow navigates the dialog
        // cross-domain (to Microsoft) and back, which can sever its
        // messageParent channel. When that happens the post-redirect page's
        // message never reaches us, but the user is still signed in via shared
        // same-origin Firebase persistence — so the dialog must close on the
        // auth-state signal even if the promise already settled some other way.
        const closeDialog = () => {
          if (closed) return;
          closed = true;
          if (closeGraceTimer) { try { clearTimeout(closeGraceTimer); } catch (e) { /* ignore */ } }
          try { unsubAuth(); } catch (e) { /* ignore */ }
          try { dialog.close(); } catch (e) { /* already closed */ }
        };
        const settle = (fn) => {
          if (settled) return;
          settled = true;
          fn();
        };

        // Safety net: close the dialog as soon as the app becomes
        // authenticated. currentUser is null when we open this dialog (we only
        // prompt when signed out), so the first truthy callback is a real
        // sign-in — whether it arrived via the bridge message below or via
        // shared persistence after the redirect.
        try {
          unsubAuth = onAuthStateChanged(auth, (user) => {
            if (user) {
              closeDialog();
              settle(() => resolve({ viaSharedAuth: true }));
            }
          });
        } catch (e) {
          // Firebase auth unavailable here — rely on the message path below.
        }

        dialog.addEventHandler(
          Office.EventType.DialogMessageReceived,
          (arg) => {
            // The dialog relays diagnostic breadcrumbs (so its behavior on Office
            // web, which has its own console we cannot see, surfaces in the TASK
            // PANE console). These are NOT the final auth result — log and keep
            // listening; do not settle/close on them.
            let parsed = null;
            try { parsed = JSON.parse(arg.message); } catch (e) { /* non-JSON */ }
            if (parsed && parsed.__diag) {
              console.log('[AuthDialog→parent]', parsed.step, parsed.detail || '');
              return;
            }
            closeDialog();
            settle(() => {
              if (parsed) {
                resolve(parsed);
              } else {
                reject(new Error('Invalid auth response from dialog'));
              }
            });
          }
        );

        dialog.addEventHandler(
          Office.EventType.DialogEventReceived,
          (arg) => {
            if (settled) return;
            // 12006 = "dialog closed". On Office DESKTOP this means a real user
            // cancel. But on Office WEB it ALSO fires when the dialog merely
            // navigates cross-origin mid-sign-in (e.g. a Microsoft redirect to a
            // domain not in the manifest AppDomains) or closes itself right after
            // messaging us — so it is NOT reliably a cancel. The dialog and task
            // pane are same-origin, so a completed sign-in propagates here via
            // the onAuthStateChanged net above (Firebase broadcasts auth state
            // across same-origin windows). So on 12006 we do NOT reject
            // immediately: we keep that net alive for a short grace window and
            // only treat it as a genuine cancel if no sign-in arrives. This is
            // what lets a sign-in that completed in the dialog (the bridge token
            // was minted) still succeed even though Office told us "closed".
            if (arg.error === 12006) {
              if (closeGraceTimer) return; // grace already running
              closeGraceTimer = setTimeout(() => {
                settle(() => {
                  try { unsubAuth(); } catch (e) { /* ignore */ }
                  reject(new Error('Auth dialog was closed by user'));
                });
              }, 6000);
            } else {
              closeDialog();
              settle(() => reject(new Error(`Auth dialog error: ${arg.error}`)));
            }
          }
        );
      }
    );
  });
}

/**
 * Send auth result from the dialog page back to the chat screen.
 * Call this from the /chat/auth page after successful sign-in.
 *
 * @param {{ token: string, user: Object }} authData
 */
export function sendAuthResultToParent(authData) {
  if (typeof Office !== 'undefined' && Office.context?.ui?.messageParent) {
    Office.context.ui.messageParent(JSON.stringify(authData));
  }
}

// Documented Office SSO error codes where the *only* sensible response is
// to fall back to the Dialog API flow — there's nothing the user (or we)
// can do to recover SSO. See:
// https://learn.microsoft.com/en-us/office/dev/add-ins/develop/troubleshoot-sso-in-office-add-ins
const SSO_FALLBACK_ERROR_CODES = new Set([
  13001, // User is not signed into Office
  13002, // User aborted consent
  13003, // Unsupported user identity (anonymous / personal account on some configs)
  13005, // Invalid token (transient — retry once via dialog)
  13006, // Client error inside Office
  13007, // Account couldn't return token (account in bad state)
  13008, // User triggered too many requests
  13010, // Anonymous user
  13012, // Add-in is running on a platform that doesn't support getAccessToken
  50001, // AAD doesn't recognize the resource
]);

/**
 * Attempt silent SSO via Office.js + the backend exchange function.
 *
 * Returns one of:
 *   { ok: true, customToken, uid, email, name } on success
 *   { ok: false, fallback: true, reason }       when the caller should fall back to Dialog API
 *   { ok: false, fallback: false, reason }      on unexpected failure (also fall back, but log)
 *
 * Designed to be called once on plugin mount before showing any sign-in UI.
 * The dialog flow remains untouched as the safety net.
 */
export async function trySsoSignIn() {
  // Guard: API surface availability. OfficeRuntime.auth is the modern
  // entry point; in older builds it lives on Office.auth instead.
  const ssoApi =
    (typeof OfficeRuntime !== 'undefined' && OfficeRuntime?.auth?.getAccessToken)
      ? OfficeRuntime.auth
      : (typeof Office !== 'undefined' && Office?.auth?.getAccessToken)
        ? Office.auth
        : null;

  if (!ssoApi) {
    return { ok: false, fallback: true, reason: 'SSO API not available' };
  }

  // Hard timeout: getAccessToken can sit pending indefinitely if Office is
  // mid-consent or the host webview wedges. Without this the caller's
  // "checking" spinner would never clear. 12s is generous for a silent
  // token; a real prompt-driven flow that takes longer should fall back to
  // the Dialog API anyway.
  const SSO_TIMEOUT_MS = 12000;
  let ssoToken;
  try {
    ssoToken = await Promise.race([
      ssoApi.getAccessToken({
        // Allow Office to show its own consent prompt the first time per
        // user. After consent is granted the API returns silently.
        allowSignInPrompt: true,
        // Don't ask for user consent to access additional resources beyond
        // the basic profile — keep the SSO surface minimal.
        allowConsentPrompt: true,
        // Force a fresh token (skips cache). Off by default — we want the
        // cached token when present for speed.
        forMSGraphAccess: false,
      }),
      new Promise((_, reject) =>
        setTimeout(() => reject({ code: 0, message: `SSO timed out after ${SSO_TIMEOUT_MS}ms` }), SSO_TIMEOUT_MS)
      ),
    ]);
  } catch (err) {
    const code = Number(err?.code);
    const fallback = SSO_FALLBACK_ERROR_CODES.has(code) || !code;
    return {
      ok: false,
      fallback,
      reason: `getAccessToken failed (code ${code || 'unknown'}): ${err?.message || err}`,
      errorCode: code,
    };
  }

  if (!ssoToken || typeof ssoToken !== 'string') {
    return { ok: false, fallback: true, reason: 'No SSO token returned' };
  }

  // Exchange the Office SSO token for a Firebase custom token.
  //
  // SAME-ORIGIN ONLY: the taskpane is served from re-stack.com, and a
  // cross-origin POST to cloudfunctions.net stalls inside the Office Mac
  // webview (the request never completes, with no CORS rejection — it just
  // hangs). We route through a Firebase Hosting rewrite
  // (/api/auth/office-sso → exchangeOfficeSsoToken) so the call is
  // same-origin (no CORS preflight) and behaves like the streaming
  // service calls that already work in the taskpane.
  //
  // AbortController timeout guarantees the promise settles even if the
  // network wedges — without it a hung exchange leaves the sign-in spinner
  // looping forever (the exact bug this replaces).
  const EXCHANGE_TIMEOUT_MS = 15000;
  const exchangeUrl = '/api/auth/office-sso';
  let status = 0;
  let bodyText = '';
  try {
    console.log('[OfficeAuth] SSO token acquired, exchanging via', exchangeUrl);
    const controller = new AbortController();
    // Single timer covers BOTH the fetch AND the body read. Clearing it the
    // instant headers arrive (before reading the body) was a latent hang:
    // if response.json()/text() stalled, nothing aborted it. Keep the abort
    // active until the body is fully read.
    const timer = setTimeout(() => controller.abort(), EXCHANGE_TIMEOUT_MS);
    try {
      const exchangeRes = await fetch(exchangeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ssoToken }),
        signal: controller.signal,
      });
      status = exchangeRes.status;
      console.log('[OfficeAuth] exchange responded', status);
      bodyText = await exchangeRes.text();
    } finally {
      clearTimeout(timer);
    }
  } catch (err) {
    const aborted = err?.name === 'AbortError';
    return {
      ok: false,
      fallback: true,
      reason: aborted
        ? `Exchange timed out after ${EXCHANGE_TIMEOUT_MS}ms`
        : `Exchange request failed: ${err?.message || err}`,
    };
  }

  let data = {};
  try { data = JSON.parse(bodyText) || {}; } catch { /* leave as {} */ }

  if (status < 200 || status >= 300) {
    return {
      ok: false,
      fallback: true,
      reason: `Exchange returned ${status}: ${data.error || 'error'}`,
    };
  }
  if (!data.customToken) {
    return { ok: false, fallback: true, reason: 'Exchange response missing customToken' };
  }
  return {
    ok: true,
    customToken: data.customToken,
    uid: data.uid,
    email: data.email,
    name: data.name,
  };
}
